import Icon from "@Components/generic/icon";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import useI18n from "@i18n";
import { CardActionArea, Drawer, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

interface OptionsDrawerProps {
  open: boolean;
  options: string[];
  defaultIndex?: number;
  title?: string;
  onClose: () => void;
  onOptionSelected: (option: string) => void;
  anchor?: "top" | "bottom" | "left" | "right";
  selectionDelay?: number;
  labelI18nPrefix?: string;
  optionIconResolver?: (option: string) => IconDefinition;
  selectedOption: string;
}

const OptionsDrawer = ({
  open,
  options,
  defaultIndex = 0,
  title,
  onClose,
  onOptionSelected,
  anchor = "bottom",
  selectionDelay = 150,
  labelI18nPrefix,
  optionIconResolver,
  selectedOption,
}: OptionsDrawerProps) => {
  const theme = useTheme();
  const { i18n } = useI18n();

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          margin: theme.spacing(2),
          borderRadius: theme.spacing(2),
          padding: theme.spacing(2),
        },
      }}
    >
      <Stack spacing={2}>
        {title && (
          <Typography variant="subtitle1" textAlign={"center"} color="dsBase.v200">
            {i18n("title.filter-by-metric")}
          </Typography>
        )}
        <FormControl>
          <RadioGroup
            row
            sx={{
              width: "100%",
            }}
            value={selectedOption}
            onChange={(e) => {
              onOptionSelected(e.target.value);
              setTimeout(() => {
                onClose();
              }, selectionDelay);
            }}
          >
            {options.map((value: string, index: number) => {
              return (
                <CardActionArea sx={{ marginBottom: theme.spacing(1) }} key={index}>
                  <Stack direction={"row"} alignItems="center">
                    {optionIconResolver && (
                      <Box
                        sx={{
                          backgroundColor: theme.palette.dsSurface.v200,
                          boxSizing: "border-box",
                          borderRadius: "50%",
                          padding: theme.spacing(1),
                        }}
                      >
                        <Icon
                          icon={optionIconResolver(value)}
                          color={theme.palette.dsNeutral.v500}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: theme.spacing(0.5),
                            width: theme.spacing(2.5),
                            height: theme.spacing(2.5),
                          }}
                        />
                      </Box>
                    )}
                    <FormControlLabel
                      sx={{ width: "100%", justifyContent: "space-between", fontWeight: 600, color: "dsBase.v200" }}
                      value={value}
                      control={<Radio sx={{}} />}
                      label={i18n(`${labelI18nPrefix || ""}${value.toLowerCase()}`)}
                      labelPlacement="start"
                    />
                  </Stack>
                </CardActionArea>
              );
            })}
          </RadioGroup>
        </FormControl>
      </Stack>
    </Drawer>
  );
};

export default OptionsDrawer;
